import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Save, Shield } from 'lucide-react';
import { Button } from '@/components/ui/button';
import toast from 'react-hot-toast';

interface Role {
  id?: string;
  name: string;
  description: string;
  permissions: string[];
}

interface RoleFormProps {
  roleId?: string;
}

const availablePermissions = [
  { id: 'users.read', label: 'View Users', description: 'Can view user profiles and information' },
  { id: 'users.write', label: 'Manage Users', description: 'Can create, edit, and delete users' },
  { id: 'forms.read', label: 'View Forms', description: 'Can view forms and submissions' },
  { id: 'forms.write', label: 'Manage Forms', description: 'Can create, edit, and delete forms' },
  { id: 'analytics.read', label: 'View Analytics', description: 'Can access analytics and reports' },
  { id: 'settings.read', label: 'View Settings', description: 'Can view system settings' },
  { id: 'settings.write', label: 'Manage Settings', description: 'Can modify system settings' },
  { id: 'roles.read', label: 'View Roles', description: 'Can view roles and permissions' },
  { id: 'roles.write', label: 'Manage Roles', description: 'Can create, edit, and delete roles' },
];

export const RoleForm = ({ roleId }: RoleFormProps) => {
  const navigate = useNavigate();
  const [role, setRole] = useState<Role>({
    name: '',
    description: '',
    permissions: [],
  });
  const [loading, setLoading] = useState(false);

  const isEditing = Boolean(roleId);

  useEffect(() => {
    if (isEditing && roleId) {
      // Simulate loading existing role data
      setLoading(true);
      setTimeout(() => {
        setRole({
          id: roleId,
          name: 'Editor',
          description: 'Can manage forms and view analytics',
          permissions: ['forms.read', 'forms.write', 'analytics.read'],
        });
        setLoading(false);
      }, 500);
    }
  }, [roleId, isEditing]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      toast.success(isEditing ? 'Role updated successfully!' : 'Role created successfully!');
      navigate('/users');
    } catch (error) {
      toast.error('Failed to save role');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: keyof Omit<Role, 'permissions'>, value: string) => {
    setRole(prev => ({ ...prev, [field]: value }));
  };

  const handlePermissionToggle = (permissionId: string) => {
    setRole(prev => ({
      ...prev,
      permissions: prev.permissions.includes(permissionId)
        ? prev.permissions.filter(p => p !== permissionId)
        : [...prev.permissions, permissionId]
    }));
  };

  return (
    <div className="min-h-screen bg-background p-6">
      <div className="max-w-4xl mx-auto space-y-6">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate('/users')}
            className="shrink-0"
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary/10 rounded-lg">
              <Shield className="h-6 w-6 text-primary" />
            </div>
            <div>
              <h1 className="text-2xl font-semibold text-foreground">
                {isEditing ? 'Edit Role' : 'Create New Role'}
              </h1>
              <p className="text-muted-foreground">
                {isEditing ? 'Update role permissions and settings' : 'Define a new role with specific permissions'}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-card rounded-lg border border-border">
          <form onSubmit={handleSubmit} className="p-6 space-y-6">
            <div className="space-y-6">
              <div className="space-y-2">
                <label className="text-sm font-medium text-foreground">
                  Role Name *
                </label>
                <input
                  type="text"
                  value={role.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  className="w-full px-3 py-2 border border-input rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                  placeholder="Enter role name"
                  required
                />
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-foreground">
                  Description
                </label>
                <textarea
                  value={role.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  className="w-full px-3 py-2 border border-input rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring resize-none"
                  placeholder="Describe this role and its purpose"
                  rows={3}
                />
              </div>

              <div className="space-y-4">
                <label className="text-sm font-medium text-foreground">
                  Permissions
                </label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {availablePermissions.map((permission) => (
                    <div
                      key={permission.id}
                      className="flex items-start gap-3 p-3 border border-input rounded-lg hover:bg-accent/50 transition-colors"
                    >
                      <input
                        type="checkbox"
                        id={permission.id}
                        checked={role.permissions.includes(permission.id)}
                        onChange={() => handlePermissionToggle(permission.id)}
                        className="mt-0.5"
                      />
                      <div className="flex-1">
                        <label
                          htmlFor={permission.id}
                          className="text-sm font-medium text-foreground cursor-pointer"
                        >
                          {permission.label}
                        </label>
                        <p className="text-xs text-muted-foreground mt-1">
                          {permission.description}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-3 pt-6 border-t border-border">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate('/users')}
                disabled={loading}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={loading} className="flex items-center gap-2">
                <Save className="h-4 w-4" />
                {loading ? 'Saving...' : isEditing ? 'Update Role' : 'Create Role'}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};